import React, { useState } from 'react';
import axios from 'axios';
import { Form, Field } from 'react-final-form';
import { useEffectOnce } from 'react-use';
import {
  Column,
  Card,
  Flex,
  Button,
  Row,
  Box,
} from '../../../../design-system';
import Alert from '../../../../helpers/alert';
import { successRefetch, useData } from '../../../../helpers/data-loader';
import {
  currencyFormatter,
  errorMsg,
  isExpired,
  fieldFormat,
} from '../../../../helpers/utils';
import { ExpiredCard } from '../../../../components/Expired';
import FieldColumn from '../../../../components/FieldColumn';
import FieldText from '../../../../components/FieldText';
import ModalDelete from '../../../../components/ModalDelete';
import { FIELDS } from './constants';
import { ERR_MSG } from '../../../../globalConstants';
import CardHeader from '../../../../components/CardHeader';
import DateField from '../../../../components/DateField';

interface ProgramProps {
  program: any;
  // Resolves true when the save succeeded, false otherwise.
  onSubmit: (values: any) => Promise<boolean> | void;
  navigate: (to: any) => void;
  clientId: any;
  refetch: () => void;
  canEdit: boolean;
}

const Program = ({
  program,
  onSubmit,
  navigate,
  clientId,
  refetch,
  canEdit,
}: ProgramProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, toggleDeleteModal] = useState(false);
  const { current_budget, annual_revenue } = program;

  // Leave edit mode once the save has gone through; stay in it on failure so
  // the user's changes aren't discarded.
  const handleSave = async (values: any) => {
    const saved = await onSubmit(values);
    if (saved !== false) setIsEditing(false);
  };

  const handleDeleteProgram = async () => {
    try {
      await axios.delete(`/client/${clientId}/case/${program.id}`);
      successRefetch(refetch);
      navigate(`/vault/${clientId}/program-case-statements`);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const [contacts, fetchContacts] = useData('/contact', {
    errorMsg: errorMsg('contacts'),
  });

  useEffectOnce(() => {
    fetchContacts();
  });

  const options = contacts.map(({ full_name }) => ({
    label: full_name,
    value: full_name,
  }));

  return (
    <ExpiredCard mt={2} expired={isExpired(program.expires)}>
      <CardHeader
        title={program.name}
        updatedAt={program.updated_at}
        onEditChange={setIsEditing}
        isEditing={isEditing}
        canEdit={canEdit}
        canDelete={canEdit}
        onDelete={() => toggleDeleteModal(true)}
        isSmall
      />
      <Card.Body position="relative">
        <Box>
          {isEditing ? (
            <Form
              onSubmit={handleSave}
              initialValues={{
                ...program,
                current_budget:
                  current_budget && currencyFormatter.format(current_budget),
                annual_revenue:
                  annual_revenue && currencyFormatter.format(annual_revenue),
              }}
              render={({ handleSubmit, submitting, pristine }) => (
                <form onSubmit={handleSubmit}>
                  <Row>
                    {FIELDS.map((field) => {
                      if (field.type === 'date') {
                        return (
                          <Column key={field.name} pb={2} width={300}>
                            <Field name={field.name}>
                              {({ input }) => {
                                return (
                                  <DateField
                                    name={field.name}
                                    label={field.label}
                                    selected={input.value}
                                    onChange={(value) => input.onChange(value)}
                                    showPastDue={false}
                                  />
                                );
                              }}
                            </Field>
                          </Column>
                        );
                      }

                      return (
                        <FieldColumn
                          key={field.name}
                          width={400}
                          {...field}
                          options={[
                            { label: '+ New Contact', value: 'addnew' },
                            ...options,
                          ]}
                        />
                      );
                    })}
                  </Row>
                  <Button.Group justifyContent="flex-end">
                    <Button
                      id="exit-edit-button"
                      type="submit"
                      disabled={submitting || pristine}
                    >
                      Save
                    </Button>
                  </Button.Group>
                </form>
              )}
            />
          ) : (
            <Flex flexDirection="column">
              <Row>
                {FIELDS.filter(({ name }) => name !== 'name').map((field) => (
                  <Column key={field.name} mt={2} pb={3} width={400}>
                    <FieldText data={program} {...field} />
                  </Column>
                ))}
              </Row>
            </Flex>
          )}
        </Box>
      </Card.Body>
      <ModalDelete
        entity="program"
        open={isDeleteModalOpen}
        onCancel={() => toggleDeleteModal(false)}
        onDelete={() => handleDeleteProgram()}
      />
    </ExpiredCard>
  );
};

export default Program;
